'use client';

import { useState, type KeyboardEvent } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isAfter,
  isSameDay,
  parse,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { Placeholder, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useApexCalendar } from '@/app/components/hotel-detail/hooks/useApexCalendar';
import { useTrackPriceCalendarInteraction } from '@/app/components/hotel-detail/evo/hooks/useTrackPriceCalendarInteraction';
import { useScrollToRooms } from '@/app/components/hotel-detail/evo/hooks/useScrollToRooms';
import { SERVICES_DATES_FORMAT } from '@/app/components/hotel-detail/common/dates';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import TotalPriceToggle from '@/app/common/components/TotalPriceToggle';
import { Icon } from '@/app/common/icons/Icon';
import { useTripOverviewPriceToggle } from './TripOverviewPriceToggle.tsx';
import { useApexCalendarFilters } from './ApexCalendarFiltersProvider';
import { useClickOutside } from './useClickOutside';
import {
  SelectorBodyRow,
  SelectorBody,
  SelectorHeader,
  SelectorLabel,
  SelectorRow,
  SelectorValue,
  SelectorBodyValue,
} from './apexCalendarSelectors.styles';
import {
  CalendarWrapper,
  MonthNavButton,
  MonthNavRow,
  DayHeaderRow,
  DayHeaderCell,
  WeeksContainer,
  WeekRow,
  DayCell,
  DayHeaderText,
  DayNumber,
  PriceText,
  PriceSkeleton,
  PriceSpacer,
  UnavailableText,
  CalendarFooter,
  FooterRow,
  PriceLegend,
  CheapestBadge,
  CheapestBadgeText,
  Divider,
  CalendarDaysWrapper,
} from './apexPriceCalendar.styles';

type CalendarCell = { date: Date | null; key: string };

// Enter/Space activation for the calendar's div-based buttons, delegated to the element's own
// click handler so the keyboard path can never diverge from the mouse path (same pattern as the
// search DatePicker Day cell).
function activateOnEnterOrSpace(e: KeyboardEvent<HTMLElement>): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.currentTarget.click();
  }
}

function buildCalendarDays(month: Date): Array<Date | null> {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  // getDay returns 0=Sun…6=Sat; convert to 0=Mon…6=Sun
  const startOffset = (getDay(start) + 6) % 7;
  const days: Array<Date | null> = [];

  for (let i = 0; i < startOffset; i++) days.push(null);

  let current = start;
  while (!isAfter(current, end)) {
    days.push(new Date(current));
    current = addDays(current, 1);
  }

  while (days.length % 7 !== 0) days.push(null);

  return days;
}

function getCurrencySymbol(locale: string, currency: string): string {
  if (!currency) return '';
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? currency
    );
  } catch {
    return currency;
  }
}

function buildMonthOptions(today: Date, lastProductDate: Date | null): Date[] {
  const end = lastProductDate ? startOfMonth(lastProductDate) : addMonths(startOfMonth(today), 11);
  const months: Date[] = [];
  let cursor = startOfMonth(today);
  while (!isAfter(cursor, end)) {
    months.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return months;
}

export function ApexPriceCalendar() {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();

  const dayNames = t('vacation.oss.header.widget.calendar.weekdays')
    .split('|')
    .map((d) => d.replace(/\.$/, ''));

  const { checkin, checkout, setDates } = useUrlSearchParams();
  const { resetOffers } = useOffersContext();
  const { nights, currentMonth, setCurrentMonth } = useApexCalendarFilters();
  const { setPriceContext, resetPriceContext } = useInitialParams();
  const { products, unavailableDates, currency, isLoading } = useApexCalendar();
  const trackCalendarInteraction = useTrackPriceCalendarInteraction();
  const scrollToRooms = useScrollToRooms();

  const currencyToShow = currency || localization.currency;

  const { showTotalPrice, setShowTotalPrice, showToggle } = useTripOverviewPriceToggle();
  const [monthOpen, setMonthOpen] = useState(false);
  const monthRef = useClickOutside<HTMLDivElement>(() => setMonthOpen(false));

  const today = startOfDay(new Date());

  // Sold-out days are held outside `products` (they carry no price), but they are still days the apex
  // window covers — counting them keeps the month navigation range from shrinking when the last one
  // turns out to be unavailable.
  const coveredDateKeys = [...products.keys(), ...unavailableDates];
  const lastProductDate: Date | null =
    coveredDateKeys.length > 0 ? new Date(Math.max(...coveredDateKeys.map((k) => new Date(k).getTime()))) : null;

  const monthOptions = buildMonthOptions(today, lastProductDate);

  const canGoPrev = isAfter(currentMonth, startOfMonth(today));
  const canGoNext = lastProductDate
    ? isBefore(currentMonth, startOfMonth(lastProductDate))
    : isBefore(currentMonth, addMonths(startOfMonth(today), 12));

  const currentMonthKey = format(currentMonth, 'yyyy-MM');
  const monthlyCheapestPrice = Array.from(products.entries()).reduce<number | null>((cheapest, [dateKey, product]) => {
    if (!dateKey.startsWith(currentMonthKey)) return cheapest;

    const date = parse(dateKey, 'yyyy-MM-dd', new Date());
    if (isBefore(date, today)) return cheapest;

    return cheapest === null || product.price_per_person < cheapest ? product.price_per_person : cheapest;
  }, null);

  const calendarDays = buildCalendarDays(currentMonth);
  const cells: CalendarCell[] = calendarDays.map((date, index) => ({
    date,
    key: date ? format(date, 'yyyy-MM-dd') : `pad-${index}`,
  }));
  const weeks: CalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const checkinDate = checkin ? parse(checkin, SERVICES_DATES_FORMAT, new Date()) : null;

  const handleDayClick = (date: Date, product: ApexCalendarProduct | undefined, isCheapest: boolean) => {
    if (isBefore(date, today)) return;
    const newCheckin = format(date, SERVICES_DATES_FORMAT);
    const newCheckout = format(addDays(date, nights), SERVICES_DATES_FORMAT);
    // Re-clicking the current stay must be a full no-op (checkout compared too: the nights filter can
    // change between clicks, making a same-checkin click a real change). Guarded before resetOffers —
    // resetting on a no-op wipes offersData while every SWR key (pricing, rooms) stays cached and
    // unchanged, so nothing repopulates the offers until the 29-minute pricing refresh — and before
    // setPriceContext, so a no-op click cannot store this day's rctx/rsign for a later unrelated
    // pricing request with mismatched parameters.
    if (newCheckin === checkin && newCheckout === checkout) return;
    if (product?.context) {
      setPriceContext?.(product.context.price_context, product.context.signature, product.rates);
    } else {
      // No rate context for the picked day: clear whatever the previous day (or the results page the
      // user arrived from) left behind, so the pricing for this day absorbs nothing. Without it a day
      // with no price — sold out, or a departure today — is priced against another stay's rate. The
      // context is normally consumed and cleared by the pricing request itself, but a cached pricing
      // (re-picking a day already priced in this session) fires none and leaves it in place. (MPH-904)
      resetPriceContext?.();
    }
    // Drop the previous stay's offers before changing the dates (same as PriceCalendar and
    // InlineDatePicker): a surviving sold-out offersData would render an instant "no availability"
    // for the new date and make useReportHotelNoAvailability report it to Apex (killing its rate)
    // before the rooms response confirms anything. Must happen in this handler, not in an effect:
    // batched with setDates it guarantees no render/effect ever sees new dates with stale offers.
    resetOffers();
    setDates({ checkin: newCheckin, checkout: newCheckout, nights, origin: 'APEX_CALENDAR' });
    // Tracked after the early returns: the event describes a day click that triggers a new search,
    // so a past day or a no-op re-click of the current stay must not report one.
    trackCalendarInteraction(isCheapest ? 'day_click_cheapest' : 'day_click');
    // Bring the rooms being re-priced into view a moment later (they show the loading panel meanwhile);
    // any interaction before it finishes calls it off. (MPH-971)
    scrollToRooms();
  };

  // Past dates reserve the price area with a spacer; future dates show the price, a loading skeleton,
  // a sold-out label, or a search icon when no price is available.
  const renderPriceContent = (isPast: boolean, price: number | null, isCheapest: boolean, isUnavailable: boolean) => {
    if (isLoading) {
      return isPast ? (
        <PriceSpacer />
      ) : (
        <PriceSkeleton>
          <Placeholder height="12px" width="32px" />
        </PriceSkeleton>
      );
    }
    // Ahead of the price branch on purpose — a day known to be sold out never shows a price, and
    // ahead of the search icon too, because "no availability" is more than "no price yet". (MPH-887)
    if (isUnavailable) {
      // The same key (and "N/A" copy) the legacy PriceCalendar and the search DatePicker already use
      // for an unavailable date, so the wording stays consistent across the calendars.
      return <UnavailableText>{t('vacation.select.details.pricecalendar.date.notavailable')}</UnavailableText>;
    }
    if (price !== null) {
      return (
        <PriceText $isCheapest={isCheapest} $isPast={isPast}>
          {formatPrice({ price, localization, currency: currency || undefined })}
        </PriceText>
      );
    }
    if (isPast) {
      return <PriceSpacer />;
    }
    return (
      <Icon
        category="UI Essentials"
        name="icon-magnifying-glass"
        size={16}
        color="iconSecondary"
        alt=""
        css="margin: 1px 0;"
      />
    );
  };

  return (
    <CalendarWrapper>
      <MonthNavRow>
        <MonthNavButton
          onClick={() => {
            setCurrentMonth(subMonths(currentMonth, 1));
            trackCalendarInteraction('month_left_arrow');
          }}
          disabled={!canGoPrev}
          aria-label={t('vacation.select.evo_price_calendar.previous_month') || 'Previous month'}
        >
          <Icon
            category="Arrows"
            name="icon-chevron-right"
            size={20}
            color="iconPrimary"
            alt=""
            css="transform: rotate(180deg);"
          />
        </MonthNavButton>
        <SelectorRow ref={monthRef} css="flex: 1; min-width: 0;">
          <SelectorHeader
            $isOpen={monthOpen}
            onClick={() => setMonthOpen((o) => !o)}
            onKeyDown={activateOnEnterOrSpace}
            role="button"
            tabIndex={0}
            aria-expanded={monthOpen}
          >
            <SelectorLabel>{t('vacation.select.evo_price_calendar.month') || 'Month'}</SelectorLabel>
            <SelectorValue>{format(currentMonth, 'MMMM yyyy')}</SelectorValue>
          </SelectorHeader>
          {monthOpen && (
            <SelectorBody>
              {monthOptions.map((m) => {
                const key = format(m, 'yyyy-MM');
                const isSelected = key === format(currentMonth, 'yyyy-MM');
                return (
                  <SelectorBodyRow
                    key={key}
                    onClick={() => {
                      setCurrentMonth(m);
                      setMonthOpen(false);
                      trackCalendarInteraction('month_dropdown');
                    }}
                    onKeyDown={activateOnEnterOrSpace}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    <SelectorBodyValue>{format(m, 'MMMM yyyy')}</SelectorBodyValue>
                    {isSelected && <Icon category="Semantics" name="icon-check" size={20} color="iconPrimary" alt="" />}
                  </SelectorBodyRow>
                );
              })}
            </SelectorBody>
          )}
        </SelectorRow>
        <MonthNavButton
          onClick={() => {
            setCurrentMonth(addMonths(currentMonth, 1));
            trackCalendarInteraction('month_right_arrow');
          }}
          disabled={!canGoNext}
          aria-label={t('vacation.select.evo_price_calendar.next_month') || 'Next month'}
        >
          <Icon category="Arrows" name="icon-chevron-right" size={20} color="iconPrimary" alt="" />
        </MonthNavButton>
      </MonthNavRow>

      <CalendarDaysWrapper>
        <DayHeaderRow>
          {dayNames.map((name) => (
            <DayHeaderCell key={name}>
              <DayHeaderText>{name}</DayHeaderText>
            </DayHeaderCell>
          ))}
        </DayHeaderRow>

        <WeeksContainer>
          {weeks.map((week) => (
            <WeekRow key={week[0].key}>
              {week.map(({ date, key }) => {
                if (date === null) {
                  return <DayCell key={key} $isEmpty={true} $isSelected={false} $isCheapest={false} $isPast={false} />;
                }

                const isPast = isBefore(date, today);
                const product = products.get(key);
                const isUnavailable = !isPast && unavailableDates.has(key);
                const isSelected = !isPast && !!checkinDate && isSameDay(date, checkinDate);
                const isCheapest =
                  !isPast &&
                  !!product &&
                  monthlyCheapestPrice !== null &&
                  product.price_per_person === monthlyCheapestPrice;
                const price = product ? (showTotalPrice ? product.price : product.price_per_person) : null;

                return (
                  <DayCell
                    key={key}
                    $isEmpty={false}
                    $isSelected={isSelected}
                    $isCheapest={isCheapest}
                    $isPast={isPast}
                    onClick={() => handleDayClick(date, product, isCheapest)}
                    onKeyDown={activateOnEnterOrSpace}
                    role="button"
                    tabIndex={isPast ? -1 : 0}
                    aria-pressed={isPast ? undefined : isSelected}
                    aria-disabled={isPast || undefined}
                  >
                    <DayNumber $isPast={isPast} $isCheapest={isCheapest} $isUnavailable={isUnavailable}>
                      {format(date, 'd')}
                    </DayNumber>
                    {renderPriceContent(isPast, price, isCheapest, isUnavailable)}
                  </DayCell>
                );
              })}
            </WeekRow>
          ))}
        </WeeksContainer>
      </CalendarDaysWrapper>

      <CalendarFooter>
        <Divider />
        <FooterRow>
          {showToggle && (
            <TotalPriceToggle
              variant="row"
              checked={showTotalPrice}
              onChange={setShowTotalPrice}
              disabled={isLoading}
            />
          )}
          <PriceLegend>
            <CheapestBadge>
              <CheapestBadgeText>
                {getCurrencySymbol(localization.locale, currencyToShow) || currencyToShow}
              </CheapestBadgeText>
            </CheapestBadge>
            <Text variant="Body">-</Text>
            <Text variant="BodySmall">{t('vacation.select.cc.trip_overview.cheapest_price')}</Text>
          </PriceLegend>
        </FooterRow>
      </CalendarFooter>
    </CalendarWrapper>
  );
}
